import { toMoneyMinor } from './money';
import { z } from 'zod';
import { AssistantError } from './assistant';
import {
  propertyAlternativeSchema,
  propertyAmenitySchema,
  propertySearchSchema,
  propertyUnitTypesSchema,
  type PropertySearch,
} from './catalog';

const fields = propertyAlternativeSchema.shape;
export const propertyCategorySchema = z.enum([
  'apartments',
  'house',
  'studio',
  'duplex',
  'triplex',
  'penthouse',
  'townhouse_unit',
  'office',
  'retail_local',
  'warehouse_unit',
  'coworking_space',
  'lot',
  'industrial_lot',
  'agricultural_parcel',
  'hotel_room',
  'suite',
  'condo_hotel_unit',
  'garage',
  'garage_with_storage',
  'storage_unit',
  'other',
]);
const criterionChangeSchema = z.discriminatedUnion('field', [
  z
    .object({
      field: z.literal('propertyCategory'),
      value: propertyCategorySchema.nullable(),
    })
    .strict(),
  z
    .object({
      field: z.literal('unitTypes'),
      value: propertyUnitTypesSchema.nullable(),
    })
    .strict(),
  z.object({ field: z.literal('unitId'), value: fields.unitId }).strict(),
  z.object({ field: z.literal('unitCode'), value: fields.unitCode }).strict(),
  z
    .object({
      field: z.literal('minPriceUsdMinor'),
      value: fields.minPriceUsdMinor,
    })
    .strict(),
  z
    .object({
      field: z.literal('maxPriceUsdMinor'),
      value: fields.maxPriceUsdMinor,
    })
    .strict(),
  z
    .object({ field: z.literal('minBathrooms'), value: fields.minBathrooms })
    .strict(),
  z
    .object({
      field: z.literal('minParkingSpaces'),
      value: fields.minParkingSpaces,
    })
    .strict(),
  z.object({ field: z.literal('typology'), value: fields.typology }).strict(),
  z.object({ field: z.literal('bedrooms'), value: fields.bedrooms }).strict(),
  z.object({ field: z.literal('minFloor'), value: fields.minFloor }).strict(),
  z.object({ field: z.literal('maxFloor'), value: fields.maxFloor }).strict(),
  z.object({ field: z.literal('minAreaM2'), value: fields.minAreaM2 }).strict(),
  z.object({ field: z.literal('maxAreaM2'), value: fields.maxAreaM2 }).strict(),
  z
    .object({
      field: z.literal('minPricePenMinor'),
      value: fields.minPricePenMinor,
    })
    .strict(),
  z
    .object({
      field: z.literal('maxPricePenMinor'),
      value: fields.maxPricePenMinor,
    })
    .strict(),
  z
    .object({
      field: z.literal('viviendaSocial'),
      value: fields.viviendaSocial,
    })
    .strict(),
  z.object({ field: z.literal('sort'), value: fields.sort }).strict(),
  z.object({ field: z.literal('cursor'), value: fields.cursor }).strict(),
]);
const amenityChangesSchema = z
  .object({
    add: z.array(propertyAmenitySchema).max(40),
    remove: z.array(propertyAmenitySchema).max(40),
    match: z.enum(['all', 'any']).optional(),
  })
  .strict();
const changesSchema = z
  .object({
    criteria: z.array(criterionChangeSchema).max(18),
    amenities: amenityChangesSchema,
  })
  .strict();

export const propertySearchUpdateSchema = z
  .object({
    changes: changesSchema,
    alternatives: z
      .array(
        z
          .object({
            baseAlternative: z.number().int().min(0).max(4).nullable(),
            changes: changesSchema,
          })
          .strict()
      )
      .min(1)
      .max(5)
      .nullable(),
    unsupportedCriteria: z
      .object({
        add: z.array(z.string().min(1).max(500)).max(20),
        remove: z.array(z.string().min(1).max(500)).max(20),
      })
      .strict(),
  })
  .strict();
export type PropertySearchUpdate = z.infer<typeof propertySearchUpdateSchema>;
type Alternative = PropertySearch['alternatives'][number];
type Changes = z.infer<typeof changesSchema>;

function unrestrictedSearch(): Alternative {
  // Nulls mean no requested restriction; they never supply inventory or inferred customer needs.
  return {
    unitId: null,
    unitCode: null,
    amenities: null,
    minPriceUsdMinor: null,
    maxPriceUsdMinor: null,
    minBathrooms: null,
    minParkingSpaces: null,
    typology: null,
    bedrooms: null,
    minFloor: null,
    maxFloor: null,
    minAreaM2: null,
    maxAreaM2: null,
    minPricePenMinor: null,
    maxPricePenMinor: null,
    viviendaSocial: null,
    sort: 'updated_desc',
    cursor: null,
  };
}

function updateSet<T extends string>(
  current: readonly T[],
  changes: { add: readonly T[]; remove: readonly T[] }
): T[] {
  if (changes.add.some((value) => changes.remove.includes(value)))
    throw new AssistantError('PROPERTY_NEEDS_CONFLICTING_UPDATE', 422);
  return [
    ...new Set([
      ...current.filter((value) => !changes.remove.includes(value)),
      ...changes.add,
    ]),
  ];
}
function applyChanges(current: Alternative, changes: Changes): Alternative {
  const updated = { ...current };
  const seen = new Set<string>();
  const filtersChanged =
    changes.criteria.some((change) => change.field !== 'cursor') ||
    changes.amenities.add.length > 0 ||
    changes.amenities.remove.length > 0;
  if (filtersChanged) updated.cursor = null;
  for (const change of changes.criteria) {
    const field =
      change.field === 'propertyCategory' ? 'unitTypes' : change.field;
    if (seen.has(field))
      throw new AssistantError('PROPERTY_NEEDS_DUPLICATE_FIELD', 422);
    seen.add(field);
    if (filtersChanged && change.field === 'cursor' && change.value !== null)
      throw new AssistantError('PROPERTY_NEEDS_STALE_CURSOR', 422);
    if (change.field === 'propertyCategory') {
      updated.unitTypes =
        change.value === null
          ? null
          : change.value === 'apartments'
            ? ['apartment', 'studio', 'duplex', 'triplex', 'penthouse']
            : [change.value];
    } else Object.assign(updated, { [change.field]: change.value });
  }
  const amenities = updateSet(current.amenities ?? [], changes.amenities);
  updated.amenities = amenities.length ? amenities : null;
  return propertyAlternativeSchema.parse(updated);
}

export function applyPropertySearchUpdate(
  current: PropertySearch | null,
  requested: PropertySearchUpdate
): PropertySearch {
  const update = propertySearchUpdateSchema.parse(requested);
  const prior = current ? propertySearchSchema.parse(current) : null;
  const bases =
    update.alternatives ??
    (prior?.alternatives ?? [unrestrictedSearch()]).map((_value, index) => ({
      baseAlternative: prior ? index : null,
      changes: { criteria: [], amenities: { add: [], remove: [] } },
    }));
  const alternatives = bases.flatMap((item) => {
    const base =
      item.baseAlternative === null
        ? unrestrictedSearch()
        : prior?.alternatives[item.baseAlternative];
    if (!base)
      throw new AssistantError('PROPERTY_NEEDS_ALTERNATIVE_NOT_FOUND', 422);
    const criteria = [...update.changes.criteria, ...item.changes.criteria];
    const changed =
      update.alternatives !== null ||
      criteria.some((change) => change.field !== 'cursor') ||
      [update.changes.amenities, item.changes.amenities].some(
        (amenities) => amenities.add.length || amenities.remove.length
      );
    if (
      changed &&
      criteria.some(
        (change) => change.field === 'cursor' && change.value !== null
      )
    )
      throw new AssistantError('PROPERTY_NEEDS_STALE_CURSOR', 422);
    const expand = (changes: Changes): Changes[] =>
      changes.amenities.match === 'any' && changes.amenities.add.length > 1
        ? changes.amenities.add.map((amenity) => ({
            ...changes,
            amenities: { ...changes.amenities, add: [amenity], match: 'all' },
          }))
        : [changes];
    return expand(update.changes).flatMap((global) =>
      expand(item.changes).map((local) =>
        applyChanges(
          applyChanges(changed ? { ...base, cursor: null } : base, global),
          local
        )
      )
    );
  });
  return propertySearchSchema.parse({
    alternatives,
    unsupportedCriteria: updateSet(
      prior?.unsupportedCriteria ?? [],
      update.unsupportedCriteria
    ),
  });
}

// Validate explicit monetary bounds at the query boundary. This does not infer
// a budget or currency: it only rejects a typed filter that reverses a stated one.
export function assertPropertyBudgetDirection(
  search: PropertySearch,
  text: string
): void {
  const bounds = [
    ...text.matchAll(
      /(?<![\p{L}])(?<direction>no menos de|no m[aá]s de|hasta|m[aá]xim[oa]|menos de|up to|at most|under|desde|m[ií]nim[oa]|m[aá]s de|at least|over)(?:\s+de)?\s+(?<currency>S\/\.?|PEN\b|USD\b|US\$)\s*(?<amount>\d+(?:[.,]\d+)*)(?:\s*(?<scale>mil|thousand)\b)?/giu
    ),
  ];
  const requirements = new Map<string, Set<'min' | 'max'>>();
  for (const bound of bounds) {
    const { direction, currency, amount, scale } = bound.groups!;
    const minor = toMoneyMinor(amount!, scale ? 1000n : 1n);
    if (minor === null)
      throw new AssistantError('PROPERTY_BUDGET_AMOUNT_INVALID', 502);
    const unit = /^(?:S\/|PEN)/iu.test(currency!) ? 'Pen' : 'Usd';
    const kind =
      /^(?:no m[aá]s de|hasta|m[aá]xim|menos|up to|at most|under)/iu.test(
        direction!
      )
        ? 'max'
        : 'min';
    const key = `${unit}:${minor}`;
    const directions = requirements.get(key) ?? new Set<'min' | 'max'>();
    directions.add(kind);
    requirements.set(key, directions);
  }
  for (const alternative of search.alternatives) {
    for (const unit of ['Pen', 'Usd'] as const) {
      for (const direction of ['min', 'max'] as const) {
        const amount = alternative[`${direction}Price${unit}Minor`];
        if (amount === null) continue;
        const required = requirements.get(`${unit}:${BigInt(amount)}`);
        if (required?.size === 1 && !required.has(direction))
          throw new AssistantError('PROPERTY_BUDGET_DIRECTION_MISMATCH', 502);
      }
    }
  }
}
